import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, XCircle, Eraser, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

export function PersonaHealthCard() {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/verification/persona-health"); setH(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Health check failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="persona-health-card">
      <div className="flex items-center gap-2">
        <Activity size={16} className="text-rose-300" /><h3 className="text-sm font-semibold text-white">Persona connection</h3>
        {h && <span className={`text-[11px] rounded-full px-2 py-0.5 border ${h.ready ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}`} data-testid="persona-health-ready">{h.ready ? "Production-ready" : h.mode === "live" ? "Checks pending" : `mode: ${h.mode}`}</span>}
        <Button size="sm" variant="ghost" className="h-7 text-[11px] ml-auto" disabled={busy} onClick={load} data-testid="persona-health-refresh"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /></Button>
      </div>
      {!h ? <div className="text-xs text-zinc-500">Checking…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[["Mode", h.mode], ["API key", h.key_kind], ["Environment", h.environment_id || "—"], ["Template", h.template_id || "—"],
              ["Webhook secret", h.webhook_secret_set ? "set" : "missing"], ["Events (24h)", h.events_24h], ["Last event", fmt(h.last_event_at)], ["Rejected hooks", h.rejected_webhooks_open]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-black/30 px-2.5 py-2"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-zinc-100 truncate" data-testid={`persona-health-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{String(v)}</div></div>
            ))}
          </div>
          <div className="text-[11px] text-zinc-400 break-all">Webhook URL: <span className="text-zinc-200" data-testid="persona-health-url">{h.expected_webhook_url}</span></div>
          <ul className="space-y-1">
            {h.checks.map((c) => (
              <li key={c.name} className="flex items-center gap-2 text-xs" data-testid={`persona-check-${c.name}`}>
                {c.ok ? <CheckCircle2 size={13} className="text-emerald-300" /> : <XCircle size={13} className="text-rose-300" />}
                <span className="text-zinc-200">{c.name}</span><span className="text-zinc-500">· {c.detail}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function MockAuditCard({ onChanged }) {
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/admin/verification/mock-audit"); setA(data); }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const purge = async () => {
    if (!window.confirm(`Invalidate ${a.count} simulator-made record(s)? Those members will need to verify with Persona. Accounts, coins and Bamboo are untouched.`)) return;
    try { const { data } = await api.post("/admin/verification/mock-purge", { confirm: true }); toast.success(`Invalidated ${data.invalidated}`); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
  };
  if (!a) return null;
  return (
    <section className="rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4 space-y-2" data-testid="mock-audit-card">
      <div className="flex flex-wrap items-center gap-2">
        <Eraser size={16} className="text-amber-300" /><h3 className="text-sm font-semibold text-white">Simulator records</h3>
        <span className="text-xs text-zinc-400" data-testid="mock-audit-count">{a.count} total · {a.verified_count} marked Verified without Persona</span>
        {user?.is_owner && a.count > 0 && <Button size="sm" variant="outline" className="h-7 rounded-full border-amber-400/40 text-amber-200 text-[11px] ml-auto" onClick={purge} data-testid="mock-purge-btn">Invalidate all</Button>}
      </div>
      {a.count === 0 ? <div className="text-xs text-zinc-500" data-testid="mock-audit-empty">No member holds a simulator-made verification.</div> : (
        <ul className="text-xs text-zinc-300 space-y-1">
          {a.items.map((r) => <li key={r.user_id} data-testid={`mock-audit-row-${r.user_id}`}>@{r.handle || r.user_id.slice(0, 8)} · {r.status} · {fmt(r.updated_at)}</li>)}
        </ul>
      )}
    </section>
  );
}
